/* eslint-disable no-use-before-define */
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import usePlanAndLDFlag from '../../../../../hooks/usePlanAndLDFlag';
import { Button, Divider, Form, message, Skeleton } from 'antd-v5';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import { postRequest } from '../../../../../core/apiRequests';
import {
  setAuthLastName,
  setAuthMobile,
  setAuthEmail,
  setAuthFirstName,
  setAuthImage,
  setMessageNotifications,
  setOutOfOfficeResponse,
  setOutOfOfficeEnabled,
} from '../../../../../reducers/auth';
import { ProfileDetails } from './components/ProfileDetails';
import { ProfileNotifications } from './components/ProfileNotifications';
import { WorkSchedule } from './components/WorkSchedule';

const EditProfile = ({ auth }) => {
  const history = useHistory();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [uploadedImg, setUploadedImg] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [changeImage, setChangeImage] = useState(false);
  const appointmentsEnabled = usePlanAndLDFlag('appointments');

  const profileData = useMemo(
    () => ({
      firstName: auth.firstName || '',
      lastName: auth.lastName || '',
      email: auth.email || '',
      mobile: auth.mobile || '',
      image: auth.image || '',
      message_notifications: Boolean(auth.message_notifications),
      iso_country_code: auth.iso_country_code || 'US',
      outOfOfficeEnabled: Boolean(auth.outOfOfficeEnabled),
      outOfOfficeResponse: auth.outOfOfficeResponse || '',
    }),
    [auth],
  );

  const isFetching = !auth.userId;

  // Update form when auth state changes
  useEffect(() => {
    console.log('Auth changed:', profileData);
    form.setFieldsValue({
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      email: profileData.email,
      mobile: profileData.mobile,
      message_notifications: Boolean(profileData.message_notifications),
      outOfOfficeEnabled: Boolean(profileData.outOfOfficeEnabled),
      outOfOfficeResponse: profileData.outOfOfficeResponse,
    });
  }, [profileData, form]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const values = await form.validateFields();
      console.log('Form values to save:', values);
      const { userId, merchantId, image } = auth;
      const cleanImg = image === 'null' || image === '' || image === null ? null : image;

      const reqObj = {
        params: ['associates', merchantId, userId, 'updateProfile'],
        images: { files: values?.image?.originFileObj ? [values?.image?.originFileObj] : [] },
        data: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email.toLowerCase(),
          mobile: parsePhoneNumber(values.mobile),
          messageNotifications: values.message_notifications,
          image: cleanImg,
          outOfOfficeEnabled: values.outOfOfficeEnabled,
          outOfOfficeResponse: values.outOfOfficeResponse,
        },
      };

      const saveData = await postRequest(reqObj);

      // Update Redux state
      setAuthFirstName(values.firstName);
      setAuthLastName(values.lastName);
      setAuthMobile(values.mobile);
      setAuthEmail(values.email);
      setMessageNotifications(values.message_notifications);
      setOutOfOfficeResponse(values.outOfOfficeResponse);
      setOutOfOfficeEnabled(values.outOfOfficeEnabled);

      if (saveData && saveData.image) {
        setAuthImage(saveData.image);
      }

      if (saveData && saveData.userId) {
        message.success('Profile updated successfully');
        setChangeImage(false);
        setUploadedImg(null);
      } else {
        message.error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.errorFields) {
        message.error('Please check all required fields');
      } else {
        message.error('Failed to update profile. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }, [auth, uploadedImg, form]);

  const handleChangePassword = useCallback(() => {
    history.push('/EditProfile/change-password');
  }, [history]);

  const imageInfo = {
    imageUrl,
    setImageUrl,
    setImage: setUploadedImg,
    setChangeImage,
    uploadedImg,
    changeImage,
    profilePic: profileData?.image || auth.image,
  };

  const full = useMemo(() => `${profileData?.firstName || ''} ${profileData?.lastName || ''}`, [profileData]);
  const initials = useMemo(
    () =>
      full
        .split(' ')
        .map(word => word.substr(0, 1))
        .join(''),
    [full],
  );

  return (
    <StandardLayoutV2
      title="Edit Profile"
      breadcrumb={[{ title: 'Edit Profile' }]}
      classProps="flex-col-left"
      maxWidth="920px"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingRight: '32px', width: '100%', maxWidth: '920px' }}
      description="Manage your account settings"
      actions={
        <div className="flex-row gap-16">
          <Button onClick={handleChangePassword}>Change Password</Button>
          <Button type="primary" onClick={handleSave} loading={saving} disabled={isFetching || saving}>
            Save
          </Button>
        </div>
      }
    >
      {isFetching ? (
        <Skeleton active />
      ) : (
        <Form layout="vertical" style={{ width: '100%' }} form={form} initialValues={profileData} disabled={saving}>
          <ProfileDetails
            form={form}
            imageInfo={imageInfo}
            initials={initials}
            isoCountryCode={profileData?.iso_country_code}
          />
          <Divider style={{ margin: '40px 0' }} />
          <ProfileNotifications form={form} />
          <Divider style={{ margin: '40px 0' }} />
          {appointmentsEnabled && (
            <WorkSchedule
              form={form}
              associateId={auth?.userId}
              storeOptions={
                auth?.stores.filter(item => item.id !== 0).map(store => ({ label: store.name, value: store.id })) || []
              }
            />
          )}
        </Form>
      )}
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {})(EditProfile);

function parsePhoneNumber(phoneNumber) {
  if (!phoneNumber) return null;
  if (typeof phoneNumber === 'string') {
    return phoneNumber.includes('+') ? phoneNumber : `+${phoneNumber}`;
  }
  return `+${phoneNumber.countryCode}${phoneNumber.areaCode}${phoneNumber.phoneNumber}`;
}
